import type { InsightsMetric } from '@/api/insights.api';

import { formatMetricValue, STATUS_TEXT_CLASS } from './format';

/**
 * TrendChart — a responsive line chart for ONE metric's weekly series
 * with a dashed goal reference line.
 *
 * Hand-rolled inline SVG (no chart library — bundle size matters for
 * Lambda). Responsive via a fixed `viewBox` + `width: 100%`. The series
 * arrives DESCENDING from the overview (index 0 = most recent), so it's
 * reversed here to read left→old / right→now. Gaps (null weeks) break
 * the line rather than bridging across missing data; all-null and
 * single-point series degrade to a friendly empty state / lone dot.
 */

const VIEW_W = 320;
const VIEW_H = 120;
const PAD = { top: 12, right: 12, bottom: 20, left: 12 };

export function TrendChart({ metric, className }: { metric: InsightsMetric; className?: string }) {
  // Reverse to chronological (old → new) for display.
  const points = [...metric.series].reverse();
  const n = points.length;
  const present = points.flatMap((p) => (p.value === null ? [] : [p.value]));
  const hasData = present.length > 0;

  const statusClass = metric.current ? STATUS_TEXT_CLASS[metric.current.status] : 'text-kw-cerulean';

  if (!hasData) {
    return (
      <div className={`grid h-28 place-items-center text-[12px] text-kw-ghost ${className ?? ''}`}>
        No data in this window
      </div>
    );
  }

  // Include the goal in the value range so the reference line is always
  // on-canvas.
  const dataMin = Math.min(...present, metric.goal);
  const dataMax = Math.max(...present, metric.goal);
  const span = dataMax - dataMin || Math.abs(dataMax) || 1;

  const plotW = VIEW_W - PAD.left - PAD.right;
  const plotH = VIEW_H - PAD.top - PAD.bottom;

  const x = (i: number): number => (n <= 1 ? PAD.left + plotW / 2 : PAD.left + (i / (n - 1)) * plotW);
  const y = (v: number): number => PAD.top + plotH - ((v - dataMin) / span) * plotH;

  const goalY = y(metric.goal);

  // Break the stroke on gaps.
  const segments: { x: number; y: number }[][] = [];
  let run: { x: number; y: number }[] = [];
  points.forEach((p, i) => {
    if (p.value === null) {
      if (run.length) segments.push(run);
      run = [];
    } else {
      run.push({ x: x(i), y: y(p.value) });
    }
  });
  if (run.length) segments.push(run);

  return (
    <svg
      viewBox={`0 0 ${String(VIEW_W)} ${String(VIEW_H)}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`${metric.title} trend over the last ${String(n)} weeks; goal ${formatMetricValue(
        metric.goal,
        metric,
      )}`}
      className={className}
      style={{ width: '100%', height: 'auto' }}
    >
      {/* Goal reference line */}
      <line
        x1={PAD.left}
        y1={goalY}
        x2={VIEW_W - PAD.right}
        y2={goalY}
        stroke="currentColor"
        className="text-kw-ghost"
        strokeDasharray="3 3"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <text x={VIEW_W - PAD.right} y={goalY - 3} textAnchor="end" className="fill-kw-muted" fontSize={8}>
        Goal {formatMetricValue(metric.goal, metric)}
      </text>

      {/* Trend line(s), coloured by current status */}
      <g className={statusClass}>
        {segments.map((seg, si) => (
          <polyline
            key={si}
            points={seg.map((p) => `${String(p.x)},${String(p.y)}`).join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {points.map((p, i) =>
          p.value === null ? null : (
            <circle
              key={i}
              cx={x(i)}
              cy={y(p.value)}
              r={2}
              fill="currentColor"
              vectorEffect="non-scaling-stroke"
            />
          ),
        )}
      </g>
    </svg>
  );
}
